import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import OwnerDashboard from '../pages/OwnerDashboard';
import TenantDashboard from '../pages/TenantDashboard';
import ServiceProviderDashboard from '../pages/ServiceProviderDashboard';
import AdminDashboard from '../pages/AdminDashboard';

const RoleBasedDashboard = () => {
  const { currentUser, userData, loading } = useAuth();
  const [recoveryRole, setRecoveryRole] = useState('owner');
  const [recoveryName, setRecoveryName] = useState('');
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    setRecoveryError('');
    setRecoverySubmitting(true);
    try {
      await authService.createUserDocument(currentUser.uid, {
        email: currentUser.email,
        role: recoveryRole,
        displayName: recoveryName.trim() || currentUser.displayName || currentUser.email?.split('@')[0] || 'User'
      });
      window.location.reload();
    } catch (err) {
      setRecoveryError(err.message || 'Failed to save. Please try again.');
    } finally {
      setRecoverySubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!userData && currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-md w-full p-8">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Complete your profile</h2>
          <p className="text-gray-600 text-sm mb-6">
            We found your account but your profile data is missing. Choose your account type and we&apos;ll restore your dashboard.
          </p>
          <form onSubmit={handleRecoverySubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account type</label>
              <select
                value={recoveryRole}
                onChange={(e) => setRecoveryRole(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                <option value="owner">Property Owner</option>
                <option value="serviceProvider">Service Provider</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display name (optional)</label>
              <input
                type="text"
                value={recoveryName}
                onChange={(e) => setRecoveryName(e.target.value)}
                placeholder={currentUser.displayName || currentUser.email?.split('@')[0] || 'Your name'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            {recoveryError && (
              <p className="text-sm text-red-600">{recoveryError}</p>
            )}
            <button
              type="submit"
              disabled={recoverySubmitting}
              className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {recoverySubmitting ? 'Saving...' : 'Continue to dashboard'}
            </button>
          </form>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full mt-3 py-2 text-gray-600 text-sm font-medium hover:text-gray-800"
          >
            Refresh page instead
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium">Error: User data not found</div>
          <p className="mt-2 text-gray-600">Please sign in again.</p>
          <a href="/login" className="inline-block mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  // Route to appropriate dashboard based on user role
  switch (userData.role) {
    case 'admin':
      console.log('Routing to AdminDashboard');
      return <AdminDashboard />;
    case 'owner':
      console.log('Routing to OwnerDashboard');
      return <OwnerDashboard />;
    case 'tenant':
      console.log('Routing to TenantDashboard');
      return <TenantDashboard />;
    case 'serviceProvider':
      console.log('Routing to ServiceProviderDashboard');
      return <ServiceProviderDashboard />;
    default:
      console.log('Unknown role:', userData.role);
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium">Unknown user role</div>
            <p className="mt-2 text-gray-600">Role: {userData.role}</p>
            <p className="mt-2 text-gray-600">Please contact support.</p>
          </div>
        </div>
      );
  }
};

export default RoleBasedDashboard; 